import React, { useState} from 'react';
import { BeltPromotion } from '../../store/slices/beltProgressionSlice';
import { createFeedItemFromBeltPromotion } from '../../services/firebase/feed-helpers';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import BJJBelt from '../../components/TrainingComponents/BJJBelt';
import EnhancedBelt from '../../components/UI/EnhancedBelt';
import logger from '../../utils/logger';

interface ShareBeltPromotionProps {
  promotion: BeltPromotion;
  onClose: () => void;
  onSuccess?: () => void;
}

const ShareBeltPromotion: React.FC<ShareBeltPromotionProps> = ({ promotion, onClose, onSuccess }) => {
  const [isPublic, setIsPublic] = useState<boolean>(true);
  const [showOnFeed, setShowOnFeed] = useState<boolean>(true);
  const [sharing, setSharing] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleShare = async () => {
    if (!user) {
      setError('You must be logged in to share your belt promotion');
      return;
    }

    try {
      setSharing(true);
      setError(null);

      // Share the promotion to feed
      await createFeedItemFromBeltPromotion(
        promotion,
        {
          displayName: user.displayName || 'Anonymous',
          username: user.username,
          photoURL: user.photoURL
        },
        {
          isPublic,
          shownOnFeed: showOnFeed
        }
      );

      setSuccess(true);
      setSharing(false);
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      logger.error('Error sharing belt promotion:', err);
      setError('Failed to share belt promotion. Please try again.');
      setSharing(false);
    }
  };

  const handleNavigateToFeed = () => {
    navigate('/feed');
    onClose();
  };

  // Format date for display
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-md w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white">Share Belt Promotion</h2>
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mb-6">
        <div className="flex justify-center mb-4">
          {(() => {
            // Check if this is Brazilian Jiu-Jitsu
            const isBJJ = promotion.martialArt === 'Brazilian Jiu Jitsu' || 
                         promotion.martialArt === 'Brazilian Jiu-Jitsu' ||
                         promotion.martialArt?.toLowerCase().includes('jiu jitsu') ||
                         promotion.martialArt?.toLowerCase() === 'bjj';
                         
            if (isBJJ) {
              return (
                <BJJBelt
                  color={promotion.beltColor}
                  stripes={promotion.stripes}
                  height="35px"
                  width="180px"
                />
              );
            } else {
              return (
                <EnhancedBelt
                  color={promotion.beltColor}
                  stripes={promotion.stripes}
                  size="large"
                  stripeBgColor={promotion.beltColor === 'black' && promotion.stripes > 0 ? 'red' : undefined}
                  contrastColor={promotion.beltColor === 'coral' ? 'red' : undefined}
                  contrastType={promotion.beltColor === 'coral' ? 'alternate' : 'none'}
                />
              );
            }
          })()}
        </div>
        
        <h3 className="text-center font-semibold mb-2 text-gray-700 dark:text-gray-200 capitalize">
          {promotion.beltColor} Belt {promotion.stripes > 0 && `(${promotion.stripes} Stripe${promotion.stripes !== 1 ? 's' : ''})`}
        </h3>
        
        <p className="text-sm text-center text-gray-600 dark:text-gray-400">
          Promoted on {formatDate(promotion.date)}
          {promotion.academy && ` at ${promotion.academy}`}
          {promotion.instructor && ` by ${promotion.instructor}`}
        </p>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-700 dark:text-gray-200">Share on feed</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={showOnFeed}
              onChange={() => setShowOnFeed(!showOnFeed)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-700 dark:text-gray-200">Public (visible to everyone)</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input 
              type="checkbox" 
              checked={isPublic}
              onChange={() => setIsPublic(!isPublic)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {success ? (
        <div className="text-center">
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md">
            Belt promotion shared successfully!
          </div>
          <button
            onClick={handleNavigateToFeed}
            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors w-full"
          >
            Go to Feed
          </button>
        </div>
      ) : (
        <button
          onClick={handleShare}
          disabled={sharing || (!showOnFeed && !isPublic)}
          className={`px-6 py-2 rounded-md w-full transition-colors ${
            sharing 
              ? 'bg-gray-400 text-gray-700' 
              : !showOnFeed && !isPublic 
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
                : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          {sharing ? 'Sharing...' : 'Share Belt Promotion'}
        </button>
      )}
    </div>
  );
};

export default ShareBeltPromotion;